import React from 'react';
import {
  LogIn,
  LogOut,
  Clock,
  Phone,
  Mail,
  Users,
  Home,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Sparkles,
  Wrench,
  User
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getStatusColor, getTimeSlot, formatTime } from '../../utils/operationsData';
import moment from 'moment';

export const OperationCard = ({
  operation,
  onConfirm = () => { },
  onEarlyLate = () => { },
  onNoShow = () => { },
  onContact = () => { },
  onAddPayment = () => { },
}) => {
  const { t } = useTranslation();

  const statusConfig = getStatusColor(operation.operationStatus);
  const timeSlot = getTimeSlot(operation.expectedTime);
  const isOverdue = operation.type === 'checkin'
    ? new Date() > new Date(`${operation.expectedTime}:00`)
    : operation.type === 'checkout' && new Date() > new Date(`${operation.expectedTime}:00`);

  const getRoomStatusIcon = (status) => {
    switch (status) {
      case 'clean': return <Home size={14} className="text-green-600" />;
      case 'dirty': return <AlertTriangle size={14} className="text-red-600" />;
      case 'cleaning': return <Sparkles size={14} className="text-blue-600" />;
      case 'maintenance': return <Wrench size={14} className="text-orange-600" />;
      default: return <Home size={14} className="text-slate-500" />;
    }
  };

  const getRoomStatusLabel = (status) => {
    switch (status) {
      case 'clean': return t('roomStatusClean');
      case 'dirty': return t('roomStatusDirty');
      case 'cleaning': return t('roomStatusCleaning');
      case 'maintenance': return t('roomStatusMaintenance');
      default: return status;
    }
  };

  const getRequestStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'deposit': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'settled': return 'bg-green-100 text-green-800 border-green-200';
      case 'awaiting': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getRequestStatusLabel = (status) => {
    switch (status) {
      case 'settled': return t('settled');
      case 'deposit': return t('deposit');
      case 'confirmed': return t('confirmed');
      case 'pending': return t('pending');
      default: return status;
    }
  };

  return (
    <div className={`bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 transition-all duration-200 hover:shadow-lg ${isOverdue && operation.operationStatus === 'pending'
      ? 'border-red-200 bg-red-50'
      : operation.priority === 'urgent'
        ? 'border-orange-200 bg-orange-50'
        : 'border-slate-200 hover:border-slate-300'
      }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className={`p-3 rounded-xl ${operation.type === 'checkin'
            ? 'bg-green-100 text-green-600'
            : 'bg-orange-100 text-orange-600'
            }`}>
            {operation.type === 'checkin' ? <LogIn size={20} /> : <LogOut size={20} />}
          </div>
          <div>
            <h3 className="font-bold text-slate-900 text-base sm:text-lg">{operation.reqClientName}</h3>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig}`}>
                {t(`status${operation.operationStatus.charAt(0).toUpperCase() + operation.operationStatus.slice(1)}`)}
              </span>
              {/* {operation.priority === 'urgent' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                  {t('priorityUrgent')}
                </span>
              )} */}
            </div>
          </div>
        </div>

        {/* Time and Unit */}
        <div className="text-right">
          <div className="text-lg sm:text-xl font-bold text-slate-900">
            {operation.unitCode}
          </div>
          <div className="text-sm text-slate-600">{operation.unitName}</div>
          <div className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-slate-700'}`}>
            {formatTime(operation.expectedTime)}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Users size={14} className="text-slate-500" />
            <span className="text-slate-700">
              {operation.reqGuests === 1 ? t('guest') : t('guests')} {operation.reqGuests}
            </span>
          </div>

          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {getRoomStatusIcon(operation.roomStatus)}
            <span className="text-slate-700">{getRoomStatusLabel(operation.roomStatus)}</span>
          </div>

          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Clock size={14} className="text-slate-500" />
            <span className="text-slate-700">
              {/* {t(`timeSlot${timeSlot.charAt(0).toUpperCase() + timeSlot.slice(1)}`)} */}
              {moment.utc(operation.type === 'checkin' ? operation.reqFrom : operation.reqTo).format('HH:mm')}
            </span>
          </div>

          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <DollarSign size={14} className="text-slate-500" />
            <span className={`text-sm font-medium border px-2 py-1 rounded-full ${getRequestStatusColor(operation.reqStatus)}`}>
              {getRequestStatusLabel(operation.reqStatus)}
            </span>
          </div>
        </div>

        {/* Special Requests */}
        {operation.notes ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2 rtl:space-x-reverse">
              <AlertTriangle size={14} className="text-blue-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-blue-900">
                  {t('notes')}:
                </div>
                <div className="text-sm text-blue-800">{operation.notes}</div>
              </div>
            </div>
          </div>
        ) : <></>}

        {/* Remaining Balance */}
        {operation.remainingBalance && operation.remainingBalance > 0 ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-orange-900">
                {t('operationRemainingBalance')}:
              </div>
              <div className="text-sm font-bold text-orange-700">
                {operation.remainingBalance.toLocaleString()} {t('EGP')}
              </div>
            </div>
            <button
              onClick={() => onAddPayment(operation.id)}
              className="w-full mt-2 flex items-center justify-center space-x-2 rtl:space-x-reverse px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium"
            >
              <DollarSign size={16} />
              <span>{t('addDeposit') || 'Add Deposit'}</span>
            </button>
          </div>
        ) : <></>}

        {/* Overdue Alert */}
        {isOverdue && operation.operationStatus === 'pending' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <AlertTriangle size={14} className="text-red-600" />
              <span className="text-sm font-medium text-red-900">
                {operation.type === 'checkin'
                  ? t('checkInOverdue')
                  : t('checkOutOverdue')
                }
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3">
        {/* Primary Action */}
        {operation.operationStatus === 'pending' && (
          <button
            disabled={operation.remainingBalance > 0 ? true : false}
            onClick={() => onConfirm(operation.id, operation.type)}
            className="w-full flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 font-medium"
          >
            <CheckCircle size={18} />
            <span>
              {operation.type === 'checkin'
                ? t('confirmCheckIn')
                : t('confirmCheckOut')
              }
            </span>
          </button>
        )}

        {/* Secondary Actions */}
        <div className="grid grid-cols-2 gap-3">
          {/* Early/Late Action */}
          {operation.operationStatus === 'pending' && operation.type === 'checkout' && (
            <button
              disabled={operation?.checkoutRequest ? true : false}
              onClick={() => onEarlyLate(operation.id, operation.type === 'checkin' ? 'early' : 'late')}
              className="flex items-center justify-center space-x-2 rtl:space-x-reverse px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
            >
              <Clock size={16} />
              <span>
                {operation.type === 'checkin'
                  ? t('earlyCheckIn')
                  : t('earlyLateCheckOut')
                }
              </span>
            </button>
          )}

          {/* Contact Guest */}
          <button
            onClick={() => onContact(operation.phone, operation.email)}
            className="flex items-center justify-center space-x-2 rtl:space-x-reverse px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors duration-200 text-sm font-medium"
          >
            <Phone size={16} />
            <span>{t('contactGuest')}</span>
          </button>

          {/* No Show Action (only for check-ins) */}
          {operation.type === 'checkin' && operation.operationStatus === 'pending' && (
            <button
              onClick={() => onNoShow(operation.id)}
              className="w-full flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200 text-sm font-medium border border-red-200"
            >
              <AlertTriangle size={16} />
              <span>{t('markNoShow')}</span>
            </button>
          )}
        </div>

        {/* No Show Action (only for check-ins) */}
        {/* {operation.type === 'checkin' && operation.operationStatus === 'pending' && (
          <button
            onClick={() => onNoShow(operation.id)}
            className="w-full flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200 text-sm font-medium border border-red-200"
          >
            <AlertTriangle size={16} />
            <span>{t('markNoShow')}</span>
          </button>
        )} */}
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-3 border-t border-slate-200">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <User size={12} />
            <span>
              {t('booking')}: {operation.reqCode}
            </span>
          </div>
          {operation.actualTime && (
            <div className="flex items-center space-x-1 rtl:space-x-reverse">
              <Clock size={12} />
              <span>
                {t('operationDoneAt')}: {formatTime(operation.actualTime)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};